import { CUSTOM_ELEMENTS_SCHEMA, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { PaginationModule } from 'ngx-bootstrap/pagination';

import { GlobalComponentsComponent } from './player/global-components/global-components.component';
import { PickTaskComponent } from './player/components/pick-task/pick-task.component';
import { TableComponentComponent } from './player/components/table-component/table-component.component';
import { HotspotComponentComponent } from './player/components/hotspot-component/hotspot-component.component';
import { ButtonComponentComponent } from './player/components/button-component/button-component.component';
import { DownloadComponentComponent } from './player/components/download-component/download-component.component';
import { ChatComponentComponent } from './player/components/chat-component/chat-component.component';
import { FormComponentComponent } from './player/components/form-component/form-component.component';
import { QuizComponentComponent } from './player/components/quiz-component/quiz-component.component';
import { PlayerComponent } from './player/player/player.component';
import { InventoryComponent } from './player/inventory/inventory.component';
import { ChartComponentComponent } from './player/components/chart-component/chart-component.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { ScenarioVarsPipe } from './pipes/scenario-vars.pipe';
import { FormsModule } from '@angular/forms';
import { Nl2brPipe } from './pipes/nl2br.pipe';
import { NgChartsModule } from 'ng2-charts';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { LoaderComponent } from './loader/loader.component';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ServiceWorkerModule } from '@angular/service-worker';
import { UserTrackingFormComponent } from './player/user-tracking-form/user-tracking-form.component';
import { PlayerModalComponent } from './player/player-modal/player-modal.component';
import { SafePipe } from './pipes/safe.pipe';
import { UnavailableComponent } from './unavailable/unavailable.component';
import { EmbedComponentComponent } from './player/components/embed-component/embed-component.component';
import { PlayerHelpComponent } from './player/global-components/player-help/player-help.component';
import { TabsComponentComponent } from './player/components/tabs-component/tabs-component.component';
import { ComponentContainerComponent } from './player/component-container/component-container.component';

import { DragDropModule } from '@angular/cdk/drag-drop';
import { ComponentClassPipe } from './pipes/component-class.pipe';
import { ComponentStylePipe } from './pipes/component-style.pipe';
import { BlockClassPipe } from './pipes/block-class.pipe';
import { BlockStylePipe } from './pipes/block-style.pipe';
import { ScenarioStylePipe } from './pipes/scenario-style.pipe';
import { BlockAnimationPipe } from './pipes/block-animation.pipe';
import { ChatAdventureComponentComponent } from './player/components/chat/chat-adventure-component/chat-adventure-component.component';
import { ChatMessengerComponentComponent } from './player/components/chat/chat-messenger-component/chat-messenger-component.component';
import { AnimationClassPipe } from './pipes/animation-class.pipe';
import { AnimationStylePipe } from './pipes/animation-style.pipe';
import { PopoverComponent } from './player/popover/popover.component';
import { PanoramicImageComponentComponent } from './player/components/panoramic-image-component/panoramic-image-component.component';
import { NgxView360Module } from '@egjs/ngx-view360';
import { AudioComponentComponent } from './player/components/audio-component/audio-component.component';
import { VideoComponentComponent } from './player/components/video-component/video-component.component';
import { TextComponentComponent } from './player/components/text-component/text-component.component';
import { ChoicesComponentComponent } from './player/components/choices-component/choices-component.component';
import { ImageComponentComponent } from './player/components/image-component/image-component.component';
import { ShapeComponentComponent } from './player/components/shape-component/shape-component.component';
import { IconComponentComponent } from './player/components/icon-component/icon-component.component';
import { BlankComponentComponent } from './player/components/blank-component/blank-component.component';
import { ComponentWrapStylePipe } from './pipes/component-wrap-style.pipe';
import { ComponentContainerStylePipe } from './pipes/component-container-style.pipe';
import { ThumbnailPipe } from './pipes/thumbnail.pipe';
import { ProgressComponentComponent } from './player/components/progress-component/progress-component.component';
import { TimerComponentComponent } from './player/components/timer-component/timer-component.component';
import { FormatDurationPipe } from './pipes/format-duration.pipe';
import { ModelComponentComponent } from './player/components/model-component/model-component.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { ResultsComponent } from './results/results.component';

import { LoginComponent } from './auth/login/login.component';
import { ForgottenPasswordComponent } from './auth/forgotten-password/forgotten-password.component';
import { ResetPasswordComponent } from './auth/reset-password/reset-password.component';
import { RegisterComponent } from './auth/register/register.component';
import { UpdateDetailsComponent } from './auth/update-details/update-details.component';
import { ChangePasswordComponent } from './auth/change-password/change-password.component';
import { UploadAvatarComponent } from './auth/upload-avatar/upload-avatar.component';
import { MfaComponent } from './auth/mfa/mfa.component';

import { NavbarComponent } from './navbar/navbar.component';
import {
  NgcCookieConsentConfig,
  NgcCookieConsentModule,
} from 'ngx-cookieconsent';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from 'src/environments/environment';

import { interceptorProviders } from './interceptors';

import {
  SocialLoginModule,
  SocialAuthServiceConfig,
  GoogleLoginProvider,
  GoogleSigninButtonModule,
} from '@abacritt/angularx-social-login';

// AoT requires an exported function for factories
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

const cookieConfig: NgcCookieConsentConfig = {
  cookie: {
    domain: environment.cookieDomain,
  },
  palette: {
    popup: {
      background: '#000',
    },
    button: {
      background: '#rgb(86, 170, 244)',
    },
  },
  position: 'bottom-right',
  theme: 'classic',
  type: 'opt-in',
  content: {
    message: 'This website uses cookies for analytics.',
    dismiss: 'Go away',
    deny: 'Reject',
    allow: 'Accept',
    link: 'Learn more',
    href: '/cookies',
  },
};


import { LoadingSpinnerComponent } from './shared/loading-spinner/loading-spinner.component';
import { NavbarUserComponent } from './navbar-user/navbar-user.component';
import { TimeagoPipe } from './pipes/timeago.pipe';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RedeemCodeComponent } from './redeem-code/redeem-code.component';
import { PlayComponent } from './play/play.component';
import { DataTableComponent } from './shared/data-table/data-table.component';
import { FormatDataTableDataPipe } from './shared/data-table/format-data-table-data.pipe';
import { ResultsDetailedComponent } from './results-detailed/results-detailed.component';
import { SessionLogComponent } from './analytics/session-log/session-log.component';
import { SessionRouteComponent } from './analytics/session-route/session-route.component';
import { SessionResponsesComponent } from './analytics/session-responses/session-responses.component';
import { FooterComponent } from './footer/footer.component';
import { AdminComponent } from './admin/admin.component';
import { GenerateCodesFormComponent } from './admin/generate-codes-form/generate-codes-form.component';

@NgModule({
  declarations: [
    AppComponent,
    PickTaskComponent,
    TableComponentComponent,
    HotspotComponentComponent,
    ButtonComponentComponent,
    DownloadComponentComponent,
    ChatComponentComponent,
    FormComponentComponent,
    QuizComponentComponent,
    PlayerComponent,
    GlobalComponentsComponent,
    InventoryComponent,
    ChartComponentComponent,
    NotFoundComponent,
    SafeHtmlPipe,
    ScenarioVarsPipe,
    Nl2brPipe,
    LoaderComponent,
    UserTrackingFormComponent,
    PlayerModalComponent,
    SafePipe,
    UnavailableComponent,
    EmbedComponentComponent,
    PlayerHelpComponent,
    TabsComponentComponent,
    ComponentContainerComponent,
    ComponentClassPipe,
    ComponentStylePipe,
    BlockClassPipe,
    BlockStylePipe,
    ScenarioStylePipe,
    BlockAnimationPipe,
    ChatAdventureComponentComponent,
    ChatMessengerComponentComponent,
    AnimationClassPipe,
    AnimationStylePipe,
    PopoverComponent,
    PanoramicImageComponentComponent,
    AudioComponentComponent,
    VideoComponentComponent,
    TextComponentComponent,
    ChoicesComponentComponent,
    ImageComponentComponent,
    ShapeComponentComponent,
    IconComponentComponent,
    BlankComponentComponent,
    ComponentWrapStylePipe,
    ComponentContainerStylePipe,
    ThumbnailPipe,
    ProgressComponentComponent,
    TimerComponentComponent,
    FormatDurationPipe,
    ModelComponentComponent,
    DashboardComponent,
    ResultsComponent,
    LoginComponent,
    ForgottenPasswordComponent,
    ResetPasswordComponent,
    RegisterComponent,
    UpdateDetailsComponent,
    ChangePasswordComponent,
    UploadAvatarComponent,
    MfaComponent,
    NavbarComponent,
    NavbarUserComponent,
    LoadingSpinnerComponent,
    TimeagoPipe,
    RedeemCodeComponent,
    PlayComponent,
    DataTableComponent,
    FormatDataTableDataPipe,
    ResultsDetailedComponent,
    SessionLogComponent,
    SessionRouteComponent,
    SessionResponsesComponent,
    FooterComponent,
    AdminComponent,
    GenerateCodesFormComponent
  ],
  imports: [
    BrowserAnimationsModule,
    BsDropdownModule.forRoot(),
    TooltipModule.forRoot(),
    TabsModule.forRoot(),
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    TooltipModule.forRoot(),
    NgChartsModule,
    HttpClientModule,
    DragDropModule,
    ModalModule.forRoot(),
    TabsModule.forRoot(),
    PopoverModule.forRoot(),
    NgxView360Module,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    NgcCookieConsentModule.forRoot(cookieConfig),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    SocialLoginModule,
    GoogleSigninButtonModule,
    PaginationModule.forRoot(),
  ],
  providers: [
    FormatDurationPipe,
    interceptorProviders,
    {
      provide: 'SocialAuthServiceConfig',
      useValue: {
        autoLogin: false,
        lang: 'en',
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(environment.googleClientId, {
              oneTapEnabled: false,
            }),
          },
        ],
        onError: (err: any) => console.error(err),
      },
    },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
